import { useSyncExternalStore } from 'react';

/**
 * Cross-cutting UI preferences that don't belong to any single feature.
 * Right now this is just the right members-panel toggle, but anything
 * that's "user chose this layout on this device" goes here (e.g. compact
 * vs cozy message density when we add it).
 *
 * localStorage-backed for per-device persistence. Same shape pattern as
 * lib/muteSettings.ts.
 */

interface UiPrefs {
  membersPanelOpen: boolean;
  /** Width (px) of the left channel/DM sidebar in the HeroUI shell. */
  sidebarWidth: number;
  /** Width (px) of the right members panel in the HeroUI shell. */
  membersWidth: number;
  /**
   * Silence the app's OWN cues — join/leave chimes, the message ping, the
   * screen-share chime. Strictly the sounds Bunker generates about itself:
   * people's voices, shared screen sound and voice messages are content and
   * are never affected. Per-device, like every other preference here.
   */
  systemSoundsMuted: boolean;
  /** Focus mode is remembered per channel instead of leaking across tabs. */
  focusModeByChannel: Record<string, boolean>;
  /**
   * Version 2 makes Focus opt-in. Version 1 entered it automatically on join,
   * so its saved `true` values cannot be treated as deliberate user choices.
   */
  focusModeVersion: 2;
}

const STORAGE_KEY = 'bunker.uiPrefs.v1';
const DEFAULTS: UiPrefs = {
  membersPanelOpen: true,
  sidebarWidth: 288,
  membersWidth: 264,
  systemSoundsMuted: false,
  focusModeByChannel: {},
  focusModeVersion: 2,
};

/** Allowed drag range for the resizable side panels. */
export const SIDEBAR_MIN = 220;
export const SIDEBAR_MAX = 460;
export const MEMBERS_MIN = 200;
export const MEMBERS_MAX = 420;

export function clampSidebarWidth(w: number): number {
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(w)));
}
export function clampMembersWidth(w: number): number {
  return Math.min(MEMBERS_MAX, Math.max(MEMBERS_MIN, Math.round(w)));
}

let cache: UiPrefs | null = null;
const listeners = new Set<() => void>();

function load(): UiPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const saved = JSON.parse(raw) as Partial<UiPrefs> & { focusModeVersion?: number };
    return {
      ...DEFAULTS,
      ...saved,
      // Clear values created by the old auto-focus behaviour once. From v2
      // onward every `true` comes from an explicit click or keyboard shortcut.
      focusModeByChannel: saved.focusModeVersion === 2 ? (saved.focusModeByChannel ?? {}) : {},
      focusModeVersion: 2,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(next: UiPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — in-memory only */
  }
}

export function getUiPrefs(): UiPrefs {
  if (!cache) cache = load();
  return cache;
}

export function updateUiPrefs(patch: Partial<UiPrefs>) {
  const next = { ...getUiPrefs(), ...patch };
  cache = next;
  save(next);
  for (const fn of listeners) fn();
}

export function isChannelFocusMode(channelId: string | null): boolean {
  return !!channelId && getUiPrefs().focusModeByChannel[channelId] === true;
}

export function setChannelFocusMode(channelId: string, enabled: boolean): void {
  updateUiPrefs({
    focusModeByChannel: {
      ...getUiPrefs().focusModeByChannel,
      [channelId]: enabled,
    },
  });
}

export function subscribeUiPrefs(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Are the app's own cues silenced? Read by lib/sounds on every cue. */
export function areSystemSoundsMuted(): boolean {
  return getUiPrefs().systemSoundsMuted;
}

export function useSystemSoundsMuted(): boolean {
  return useSyncExternalStore(subscribeUiPrefs, areSystemSoundsMuted, () => false);
}
